import React, { useCallback, useEffect, useState } from "react";
import { Plus, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/Layout";
import { Chip } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";
const LEVEL_CLS = {
  full: "text-[#00FF66]", manage: "text-[#4DA3FF]", approve: "text-[#C77DFF]",
  edit: "text-[#FFD600]", create: "text-zinc-200", view: "text-zinc-400", none: "text-zinc-700",
};
const labelize = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [levels, setLevels] = useState(["none", "view", "create", "edit", "approve", "manage", "full"]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", department: "", scope: "assigned", modules: {} });

  const load = useCallback(() => {
    api.get("/rbac/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/rbac/modules").then((r) => { setModules(r.data.modules); setLevels(r.data.levels); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setLevel = (mod, lvl) => setForm((f) => ({ ...f, modules: { ...f.modules, [mod]: lvl } }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Role name is required");
    try {
      await api.post("/rbac/roles", { ...form, modules: Object.fromEntries(Object.entries(form.modules).filter(([, v]) => v && v !== "none")) });
      toast.success("Custom role created");
      setOpen(false); setForm({ name: "", department: "", scope: "assigned", modules: {} }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Roles & Access" subtitle={`${roles.length} roles · module permissions + data scope`}
        actions={<button data-testid="create-role-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> Create Role</button>} />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r) => {
            const grants = Object.entries(r.modules || {}).filter(([, v]) => v && v !== "none");
            return (
              <div key={r.key} data-testid={`role-card-${r.key}`} className="bg-[#121212] border border-white/10 rounded-sm p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-medium flex items-center gap-2">{r.name}
                      {r.is_admin && <ShieldCheck size={15} className="text-[#00FF66]" />}
                    </h3>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">{r.department || "—"}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {r.custom ? <Chip className="bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]">Custom</Chip>
                      : <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-600 flex items-center gap-1"><Lock size={9} /> System</span>}
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Scope: {r.scope}</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10">
                  {r.is_admin ? <span className="text-xs font-mono text-[#00FF66]">Full access · all modules</span> : (
                    <div className="flex flex-wrap gap-1.5">
                      {grants.length === 0 && <span className="text-xs text-zinc-600 font-mono">Baseline (My Tasks only)</span>}
                      {grants.map(([m, lvl]) => (
                        <span key={m} className="text-[10px] font-mono border border-white/10 rounded-sm px-1.5 py-0.5">
                          {labelize(m)} <span className={LEVEL_CLS[lvl]}>· {lvl}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create Custom Role</DialogTitle>
            <DialogDescription className="sr-only">Define a role name, scope and module permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="role-name-input" className={inputCls} placeholder="Role name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <input className={inputCls} placeholder="Department" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Data scope</label>
              <select data-testid="role-scope-select" className={inputCls} value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}>
                {["own", "assigned", "team", "brand", "hub", "department", "all"].map((s) => <option key={s} value={s}>{labelize(s)}</option>)}
              </select>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 pt-1">Module permissions</div>
            <div className="border border-white/10 rounded-sm divide-y divide-white/5 max-h-64 overflow-y-auto">
              {modules.map((m) => (
                <div key={m} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-zinc-300">{labelize(m)}</span>
                  <select data-testid={`role-mod-${m}`} value={form.modules[m] || "none"} onChange={(e) => setLevel(m, e.target.value)}
                    className="bg-[#050505] border border-white/15 text-xs text-white rounded-sm px-2 py-1 focus:outline-none focus:border-white">
                    {levels.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button data-testid="role-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Create Role</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
